import React, { useEffect, useRef, useState } from "react";
import { MessageSquareText, X, Send, Sparkles, MessageCircle } from "lucide-react";
import { useLang } from "../LanguageContext";
import { CHAT_COPY, getSessionId, readSse, renderText } from "./chatUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const withLastAssistant = (list, content) => {
  const copy = [...list];
  const last = copy[copy.length - 1];
  if (last?.role === "assistant") copy[copy.length - 1] = { ...last, content };
  else copy.push({ id: newId(), role: "assistant", content });
  return copy;
};

const ChatBubble = ({ msg, typing }) => (
  <div className={`chat-msg ${msg.role}`} data-testid={`chat-msg-${msg.role}`}>
    {msg.content ? renderText(msg.content) : typing ? <span className="chat-typing"><i /><i /><i /></span> : ""}
  </div>
);

const ChatHeader = ({ copy, onClose }) => (
  <div className="chat-head">
    <span className="chat-avatar"><Sparkles size={16} /></span>
    <div className="flex-1 min-w-0">
      <div className="font-display text-[16px] leading-tight">{copy.title}</div>
      <div className="text-[11.5px] text-white/75">{copy.sub}</div>
    </div>
    <button type="button" onClick={onClose} className="chat-close" data-testid="chat-close-button" aria-label="Close">
      <X size={18} />
    </button>
  </div>
);

const ChatFooter = ({ copy }) => (
  <div className="chat-foot">
    <span>{copy.disclaimer}</span>
    <a href="https://wa.me/447383513170" target="_blank" rel="noopener noreferrer" data-testid="chat-whatsapp-link">
      <MessageCircle size={12} /> WhatsApp
    </a>
  </div>
);

const useChatSession = (open) => {
  const [messages, setMessages] = useState([]);
  const sid = useRef(null);

  useEffect(() => {
    if (!open || sid.current) return;
    sid.current = getSessionId();
    fetch(`${API}/chat/${sid.current}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((h) => setMessages((m) => (m.length ? m : h.map((x) => ({ id: newId(), role: x.role, content: x.content })))))
      .catch(() => {});
  }, [open]);

  return { messages, setMessages, sid };
};

export const ChatWidget = () => {
  const { lang } = useLang();
  const copy = CHAT_COPY[lang] || CHAT_COPY.en;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const { messages, setMessages, sid } = useChatSession(open);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { id: newId(), role: "user", content: msg }, { id: newId(), role: "assistant", content: "" }]);
    let acc = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      await readSse(res, (ev) => {
        if (ev.error) throw new Error(ev.error);
        if (!ev.delta) return;
        acc += ev.delta;
        setMessages((m) => withLastAssistant(m, acc));
      });
    } catch {
      setMessages((m) => withLastAssistant(m, copy.error));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="chat-fab" data-testid="chat-open-button" aria-label={copy.open}>
        <MessageSquareText size={20} /> <span>{copy.open}</span>
      </button>
    );
  }

  return (
    <div className="chat-panel" data-testid="chat-panel" role="dialog" aria-label={copy.title}>
      <ChatHeader copy={copy} onClose={() => setOpen(false)} />

      <div className="chat-body" data-testid="chat-messages">
        <div className="chat-msg assistant">{copy.welcome}</div>
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            {copy.chips.map((q, i) => (
              <button key={q} type="button" onClick={() => send(q)} className="chat-chip" data-testid={`chat-chip-${i}`}>{q}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <ChatBubble key={m.id} msg={m} typing={busy && i === messages.length - 1} />
        ))}
        <div ref={bottomRef} />
      </div>

      <form className="chat-input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={copy.placeholder} className="chat-input" maxLength={2000} data-testid="chat-input" />
        <button type="submit" disabled={busy || !input.trim()} className="chat-send" data-testid="chat-send-button" aria-label="Send">
          <Send size={16} />
        </button>
      </form>
      <ChatFooter copy={copy} />
    </div>
  );
};
